import { useState, useEffect, useCallback, useMemo, useRef } from "react";

/**
 * useLicenseCountdown hook for Player
 * 
 * Provides a real-time countdown to license expiry with SERVER-SYNCED timing.
 * Uses msRemaining and serverNow from the server to compensate for clock drift.
 * 
 * Priority:
 * 1. If msRemaining + serverNow provided → use server-synced countdown (most accurate)
 * 2. If expiresAt provided → calculate from date+time (accurate to timezone)
 * 3. Fallback → calculate from date only, assume 23:59:59
 * 
 * @param {string|Date} expiryDate - License expiry date (YYYY-MM-DD format)
 * @param {string} [expiresAt] - Optional expiry time (HH:MM:SS format)
 * @param {number} [msRemaining] - Server-provided milliseconds remaining (synced)
 * @param {number} [serverNow] - Server timestamp when msRemaining was calculated
 * @returns {Object} { days, hours, minutes, seconds, expired, formatted, compact, loading }
 */
export function useLicenseCountdown(expiryDate, expiresAt = null, msRemaining = null, serverNow = null) {
  // Track the server-synced expiry timestamp
  const serverExpiryRef = useRef(null);

  // Initialize with loading state to prevent flash of incorrect data
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
    totalSeconds: 0,
    loading: true,
  });

  // Calculate server-synced expiry timestamp when we receive msRemaining
  useEffect(() => {
    if (msRemaining != null && serverNow != null) {
      // Calculate the exact expiry time based on server's perspective
      // expiryTime = serverNow + msRemaining
      // But we need to convert to local time: localExpiry = expiryTime + (Date.now() - serverNow)
      // Simplified: localExpiry = Date.now() + msRemaining (assuming network latency is minimal)
      const localExpiry = Date.now() + msRemaining;
      serverExpiryRef.current = localExpiry;
      console.log("[LicenseCountdown] Server-synced expiry:", new Date(localExpiry).toISOString(),
        `(msRemaining: ${msRemaining}, drift: ${Date.now() - serverNow}ms)`);
    }
  }, [msRemaining, serverNow]);

  // Calculate expiry timestamp from date/time (fallback method)
  const localExpiryTimestamp = useMemo(() => {
    if (!expiryDate) return null;

    let date;

    // If expiryDate already includes time (ISO format with T), use it directly
    if (typeof expiryDate === "string" && expiryDate.includes("T")) {
      date = new Date(expiryDate);
    } else {
      // Parse as date-only (YYYY-MM-DD)
      date = new Date(expiryDate);
      if (isNaN(date.getTime())) return null;

      // Apply the expiry time
      if (expiresAt && typeof expiresAt === "string") {
        const timeParts = expiresAt.split(":").map(Number);
        const hours = timeParts[0] ?? 23;
        const minutes = timeParts[1] ?? 59;
        const seconds = timeParts[2] ?? 59;
        date.setHours(hours, minutes, seconds, 999);
      } else {
        // Default: end of day (23:59:59.999 local time)
        date.setHours(23, 59, 59, 999);
      }
    }

    if (isNaN(date.getTime())) return null;
    return date.getTime();
  }, [expiryDate, expiresAt]);

  // Get the best available expiry timestamp
  const getExpiryTimestamp = useCallback(() => {
    // Prefer server-synced expiry if available
    if (serverExpiryRef.current != null) {
      return serverExpiryRef.current;
    }
    // Fallback to locally calculated expiry
    return localExpiryTimestamp;
  }, [localExpiryTimestamp]);

  const calculateRemaining = useCallback(() => {
    const expiryTimestamp = getExpiryTimestamp();

    // If no expiry data yet, return loading state
    if (!expiryTimestamp) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: false,
        totalSeconds: 0,
        loading: true,
      };
    }

    const now = Date.now();
    const diff = expiryTimestamp - now;

    if (diff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: true,
        totalSeconds: 0,
        loading: false,
      };
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      expired: false,
      totalSeconds,
      loading: false,
    };
  }, [getExpiryTimestamp]);

  useEffect(() => {
    // Initial calculation
    setTimeRemaining(calculateRemaining());

    // Update every second
    const interval = setInterval(() => {
      setTimeRemaining(calculateRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateRemaining]);

  // Formatted string for display
  const formatted = useMemo(() => {
    const { days, hours, minutes, seconds, expired, loading } = timeRemaining;

    if (loading) return "Loading...";
    if (expired) return "Expired";

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }, [timeRemaining]);

  // Compact format for banners
  const compact = useMemo(() => {
    const { days, hours, minutes, seconds, expired, loading } = timeRemaining;

    if (loading) return "...";
    if (expired) return "Expired";

    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${seconds}s`;
  }, [timeRemaining]);

  return {
    ...timeRemaining,
    formatted,
    compact,
  };
}

export default useLicenseCountdown;
